import React, { useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { useTemplateDetailsContext } from '../../../_shared/Details/context';
import useManageTemplates from '../../../_shared/_data_sources/useManageTemplates';
import { cleanTemplateForApi } from '../../../_shared/_data_sources/useTemplateAutoSave';
import { Template } from '../../../_shared/_types';
import { useQueryClient } from 'react-query';
import { ServerErrorResponse } from 'data_sources/constants';
import TemplateForm from '../../../_shared/_components/TemplateForm';
import Loading from 'views/App/Portal/Loading';

const EditTemplate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const { template, isLoading, isDraft } = useTemplateDetailsContext();
  const { updateTemplate } = useManageTemplates();
  const queryClient = useQueryClient();
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();

  const getDetailsUrl = () => {
    const baseUrl = `/admin/performance-reporting/templates/details/${id}`;
    return isDraft ? `${baseUrl}?isDraft=true` : baseUrl;
  };

  // Check permissions and redirect if user doesn't have manage templates permission
  useEffect(() => {
    if (!hasPermission('manageFieldBank', currentOrganizationId || '')) {
      navigate('/admin/performance-reporting/templates');
    }
  }, [hasPermission, currentOrganizationId, navigate]);

  const handleSave = async (formData: Partial<Template>) => {
    if (!template) return;

    const updatedTemplate: Template = {
      ...template,
      ...formData,
    };

    // Clean the template for API before sending
    const cleanedTemplate = cleanTemplateForApi(updatedTemplate);

    await updateTemplate(
      cleanedTemplate,
      undefined, // No change note required for description updates
      (response) => {
        enqueueSnackbar(t('performanceReporting.templates.edit.messages.success'), {
          variant: 'success',
          autoHideDuration: 3000,
        });
        queryClient.invalidateQueries(['template', Number(id)]);
        queryClient.invalidateQueries(['templates']);
        navigate(getDetailsUrl());
      },
      (error) => {
        enqueueSnackbar(t('performanceReporting.templates.edit.messages.error'), {
          variant: 'error',
          autoHideDuration: 5000,
        });
        setServerErrors(error);
      }
    );
  };

  const handleCancel = () => {
    navigate(getDetailsUrl());
  };

  // Show loading while template data is being fetched
  if (isLoading) {
    return <Loading />;
  }

  // Show error if template not found
  if (!template) {
    return (
      <TakeoverLayout
        pageTitle={t('performanceReporting.templates.edit.title')}
        backUrl={getDetailsUrl()}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>{t('performanceReporting.templates.edit.messages.notFound')}</Box>
      </TakeoverLayout>
    );
  }

  return (
    <TakeoverLayout
      pageTitle={t('performanceReporting.templates.edit.title')}
      backUrl={getDetailsUrl()}
    >
      <Box>
        <TemplateForm
          onSave={handleSave}
          onCancel={handleCancel}
          serverErrors={serverErrors}
          setServerErrors={setServerErrors}
          initialValues={template}
          isEditMode={true}
          isNameEditable={true}
        />
      </Box>
    </TakeoverLayout>
  );
};

export default EditTemplate;

